import { Upload } from 'tus-js-client'
import { TestHttpStack, getBlob, wait, waitableFunction } from './helpers/utils.js'

/**
 * Helper to get body size for various input types
 */
function getBodySize(body) {
  if (body == null) return null
  if (body instanceof Blob) return body.size
  if (body.length != null) return body.length
  return 0
}

/**
 * Enhanced HTTP stack for testing stall detection scenarios
 * Supports both complete stalls and custom progress sequences
 */
class StallTestHttpStack extends TestHttpStack {
  constructor() {
    super()
    this.stallOnNextPatch = false
    this.progressSequences = new Map()
    this.progressPromises = new Map()
    this.nextProgressSequence = null
    this.methodsToStall = new Set()
  }

  /**
   * Configure the stack to stall on the next PATCH request
   */
  simulateStallOnNextPatch() {
    this.stallOnNextPatch = true
  }

  /**
   * Configure the stack to simulate a stall for a specific HTTP method
   *
   * When this is called, the specified HTTP method will stall on the next request.
   * The stall is created by returning a promise that never resolves or rejects,
   * simulating a network request that starts but never receives any data.
   *
   * @param {String} method - HTTP method to stall (e.g., 'POST', 'HEAD', 'PATCH')
   */
  simulateStallForMethod(method) {
    this.methodsToStall.add(method)
  }

  /**
   * Set a custom progress sequence for the next PATCH request
   * @param {Array} sequence - Array of {bytes: number, delay: number} objects
   */
  setNextProgressSequence(sequence) {
    this.nextProgressSequence = sequence
  }

  supportsProgressEvents() {
    return true
  }

  createRequest(method, url) {
    const req = super.createRequest(method, url)

    if (this.methodsToStall.has(method)) {
      this._setupMethodStall(req, method)
      this.methodsToStall.delete(method)
    } else if (method === 'PATCH') {
      this._setupPatchRequest(req)
    }

    return req
  }

  _setupMethodStall(req, _method) {
    const originalAbort = req.abort.bind(req)

    req.send = async function (body) {
      this.body = body

      // We create a promise but never resolve or reject it, this
      // simulates a network request that starts but never completes
      this._requestPromise = new Promise((resolve, reject) => {
        this._rejectRequest = reject
        this._resolveRequest = resolve
      })

      if (req._onRequestSend) {
        req._onRequestSend(this)
      }

      // Return the hanging promise - the caller will await this forever
      // (until StallDetector calls abort after timeout)
      return this._requestPromise
    }

    req.abort = function () {
      if (this._rejectRequest) {
        this._rejectRequest(new Error('request aborted'))
      }
      originalAbort()
    }
  }

  _setupPatchRequest(req) {
    const self = this

    if (this.stallOnNextPatch) {
      this.stallOnNextPatch = false
      req.send = async function (body) {
        this.body = body
        if (body) {
          this.bodySize = await getBodySize(body)
        }
        this._onRequestSend(this)
        return this._requestPromise
      }
      return
    }

    if (this.nextProgressSequence) {
      this.progressSequences.set(req, this.nextProgressSequence)
      this.nextProgressSequence = null
    }

    const originalRespondWith = req.respondWith.bind(req)
    req.respondWith = async (resData) => {
      const progressPromise = self.progressPromises.get(req)
      if (progressPromise) {
        await progressPromise
        self.progressPromises.delete(req)
      }
      originalRespondWith(resData)
    }

    req.send = async function (body) {
      this.body = body
      if (body) {
        this.bodySize = await getBodySize(body)
      }

      const progressSequence = self.progressSequences.get(req)
      if (progressSequence && this._onProgress) {
        self._scheduleProgressSequence(req, progressSequence, this._onProgress)
      } else if (this._onProgress) {
        self._scheduleDefaultProgress(req, this._onProgress, this.bodySize)
      }

      this._onRequestSend(this)
      return this._requestPromise
    }
  }

  _scheduleProgressSequence(req, sequence, progressHandler) {
    const progressPromise = new Promise((resolve) => {
      setTimeout(async () => {
        for (const event of sequence) {
          await new Promise((resolve) => setTimeout(resolve, event.delay || 0))
          progressHandler(event.bytes)
        }
        resolve()
      }, 10)
    })
    this.progressPromises.set(req, progressPromise)
  }

  _scheduleDefaultProgress(req, progressHandler, bodySize) {
    const progressPromise = new Promise((resolve) => {
      setTimeout(() => {
        progressHandler(0)
        progressHandler(bodySize)
        resolve()
      }, 10)
    })
    this.progressPromises.set(req, progressPromise)
  }
}

/**
 * Common test setup helper
 */
function createTestUpload(options = {}) {
  const defaultOptions = {
    httpStack: new StallTestHttpStack(),
    endpoint: 'https://tus.io/uploads',
    onError: waitableFunction('onError'),
    onSuccess: waitableFunction('onSuccess'),
    onProgress: waitableFunction('onProgress'),
  }

  const file = options.file || getBlob('hello world')
  const uploadOptions = { ...defaultOptions, ...options }
  const upload = new Upload(file, uploadOptions)

  return { upload, options: uploadOptions, testStack: uploadOptions.httpStack }
}

/**
 * Helper to handle standard upload creation flow
 */
async function handleUploadCreation(testStack, location = '/uploads/12345') {
  const req = await testStack.nextRequest()
  expect(req.method).toBe('POST')
  req.respondWith({
    status: 201,
    responseHeaders: {
      Location: location,
    },
  })
  return req
}

/**
 * Helper function to test stall detection for a specific request method
 */
async function testStallDetectionForMethod(method, uploadOptions = {}) {
  const { enableDebugLog } = await import('tus-js-client')
  enableDebugLog()

  const testStack = new StallTestHttpStack()
  testStack.simulateStallForMethod(method)

  const options = {
    httpStack: testStack,
    stallDetection: {
      enabled: true,
      checkInterval: 50,
      stallTimeout: 200,
    },
    retryDelays: null,
    ...uploadOptions,
  }

  const { upload, options: testOptions } = createTestUpload(options)

  const originalLog = console.log
  let loggedMessage = ''
  console.log = (message) => {
    loggedMessage += `${message}\n`
  }

  upload.start()

  const request = await testStack.nextRequest()
  expect(request.method).toBe(method)

  const error = await testOptions.onError.toBeCalled()

  console.log = originalLog

  return { error, loggedMessage, request }
}

describe('tus-stall-detection', () => {
  describe('integration tests', () => {
    it("should not enable stall detection if HTTP stack doesn't support progress events", async () => {
      const { enableDebugLog } = await import('tus-js-client')
      enableDebugLog()

      const testStack = new TestHttpStack()
      testStack.supportsProgressEvents = () => false

      const { upload } = createTestUpload({
        httpStack: testStack,
        stallDetection: { enabled: true },
      })

      // Capture console output
      const originalLog = console.log
      let loggedMessage = ''
      console.log = (message) => {
        loggedMessage += message
      }

      upload.start()

      const req = await testStack.nextRequest()
      expect(req.url).toBe('https://tus.io/uploads')
      expect(req.method).toBe('POST')
      req.respondWith({
        status: 201,
        responseHeaders: { Location: '/uploads/12345' },
      })

      await wait(50)
      console.log = originalLog

      expect(loggedMessage).toContain(
        'tus: stall detection is enabled but the HTTP stack does not support progress events',
      )

      upload.abort()
    })

    it('should upload a file with stall detection enabled', async () => {
      const { upload, options, testStack } = createTestUpload({
        stallDetection: {
          enabled: true,
          checkInterval: 1000,
          stallTimeout: 2000,
        },
      })

      upload.start()

      await handleUploadCreation(testStack)

      const patchReq = await testStack.nextRequest()
      expect(patchReq.url).toBe('https://tus.io/uploads/12345')
      expect(patchReq.method).toBe('PATCH')

      patchReq.respondWith({
        status: 204,
        responseHeaders: { 'Upload-Offset': '11' },
      })

      await options.onSuccess.toBeCalled()
      expect(options.onError.calls.count()).toBe(0)
    })

    it('should detect stalls and emit error when no retries configured', async () => {
      const { upload, options, testStack } = createTestUpload({
        stallDetection: {
          enabled: true,
          checkInterval: 100,
          stallTimeout: 200,
        },
        retryDelays: null,
      })

      testStack.simulateStallOnNextPatch()
      upload.start()

      await handleUploadCreation(testStack)

      const error = await options.onError.toBeCalled()
      expect(error.message).toContain('stalled:')
    })

    it('should retry when stall is detected', async () => {
      const { upload, options, testStack } = createTestUpload({
        stallDetection: {
          enabled: true,
          checkInterval: 100,
          stallTimeout: 200,
        },
        retryDelays: [100],
      })

      testStack.simulateStallOnNextPatch()
      upload.start()

      let requestCount = 0
      while (true) {
        const req = await testStack.nextRequest()
        requestCount++

        if (req.method === 'POST') {
          req.respondWith({
            status: 201,
            responseHeaders: { Location: '/uploads/12345' },
          })
        } else if (req.method === 'HEAD') {
          req.respondWith({
            status: 200,
            responseHeaders: {
              'Upload-Offset': '0',
              'Upload-Length': '11',
            },
          })
        } else if (req.method === 'PATCH') {
          req.respondWith({
            status: 204,
            responseHeaders: { 'Upload-Offset': '11' },
          })
          break
        }

        if (requestCount > 10) {
          throw new Error('Too many requests')
        }
      }

      await options.onSuccess.toBeCalled()
      expect(options.onError.calls.count()).toBe(0)
      expect(requestCount).toBeGreaterThan(1)
    })

    it('should not incorrectly detect stalls during onBeforeRequest delays', async () => {
      const { upload, options, testStack } = createTestUpload({
        stallDetection: {
          enabled: true,
          checkInterval: 100,
          stallTimeout: 200,
        },
        onBeforeRequest: async (_req) => {
          await wait(300) // Longer than stall timeout
        },
      })

      upload.start()

      await handleUploadCreation(testStack)

      const patchReq = await testStack.nextRequest()
      expect(patchReq.url).toBe('https://tus.io/uploads/12345')
      expect(patchReq.method).toBe('PATCH')

      patchReq.respondWith({
        status: 204,
        responseHeaders: { 'Upload-Offset': '11' },
      })

      await options.onSuccess.toBeCalled()
      expect(options.onError.calls.count()).toBe(0)
    })

    it('should detect stalls when progress events stop mid-upload', async () => {
      const file = getBlob('hello world'.repeat(100))
      const { upload, options, testStack } = createTestUpload({
        file,
        stallDetection: {
          enabled: true,
          checkInterval: 100,
          stallTimeout: 200,
        },
        retryDelays: null,
      })

      // Create a progress sequence that stops at 30% of the file
      const fileSize = file.size
      const progressSequence = [
        { bytes: 0, delay: 10 },
        { bytes: Math.floor(fileSize * 0.1), delay: 50 },
        { bytes: Math.floor(fileSize * 0.2), delay: 50 },
        { bytes: Math.floor(fileSize * 0.3), delay: 50 },
        // No more progress events after 30%
      ]

      testStack.setNextProgressSequence(progressSequence)
      upload.start()
      await handleUploadCreation(testStack)

      const error = await options.onError.toBeCalled()
      expect(error.message).toContain('stalled:')
      expect(options.onProgress.calls.count()).toBeGreaterThan(0)
    })

    it('should NOT detect stalls when progress value does not change but events are still fired', async () => {
      const file = getBlob('hello world')
      const { upload, options, testStack } = createTestUpload({
        file,
        stallDetection: {
          enabled: true,
          checkInterval: 50,
          stallTimeout: 500,
        },
        retryDelays: null,
      })

      // Create a progress sequence that gets stuck at 5 bytes
      // but still fires progress events (simulating NodeHttpStack buffer behavior)
      const progressSequence = [
        { bytes: 0, delay: 10 },
        { bytes: 2, delay: 10 },
        { bytes: 5, delay: 10 },
        // Repeat the same value - with the new behavior, this should NOT trigger stall detection
        // as long as progress events are still being fired
        ...Array(12).fill({ bytes: 5, delay: 30 }),
        // Eventually progress continues
        { bytes: 8, delay: 10 },
        { bytes: 11, delay: 10 },
      ]

      testStack.setNextProgressSequence(progressSequence)
      upload.start()

      await handleUploadCreation(testStack)

      const patchReq = await testStack.nextRequest()
      expect(patchReq.method).toBe('PATCH')

      // Complete the upload successfully
      patchReq.respondWith({
        status: 204,
        responseHeaders: { 'Upload-Offset': '11' },
      })

      // The upload should complete successfully without stall detection
      await options.onSuccess.toBeCalled()
      expect(options.onError.calls.count()).toBe(0)
      expect(options.onProgress.calls.count()).toBeGreaterThan(0)
    })

    it('should detect stalls during POST request (upload creation)', async () => {
      const { error, loggedMessage, request } = await testStallDetectionForMethod('POST')

      expect(request.url).toBe('https://tus.io/uploads')
      expect(error.message).toContain('request aborted')
      expect(error.message).toContain('POST')
      expect(loggedMessage).toContain('starting stall detection')
      expect(loggedMessage).toContain('upload stalled')
    })

    it('should detect stalls during HEAD request (resuming upload)', async () => {
      const { error, loggedMessage, request } = await testStallDetectionForMethod('HEAD', {
        uploadUrl: 'https://tus.io/uploads/existing',
      })

      expect(request.url).toBe('https://tus.io/uploads/existing')
      expect(error.message).toContain('request aborted')
      expect(error.message).toContain('HEAD')
      expect(loggedMessage).toContain('starting stall detection')
      expect(loggedMessage).toContain('upload stalled')
    })
  })
})
